import { Component, OnInit } from '@angular/core';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { createRedirectionUrlFromNavigationOptions } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BaseCssProperties,
  DummyRole,
  NavigationOptionEnum,
  NavigationOptionsType,
  ProviderClass,
  QuickLinksWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { createLoggedOutModalPageUrl } from '../../../zones/modal-utils';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { BaseComponent } from '../base/base.component';
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-multi-icon-quick-link-widget',
  templateUrl: './multi-icon-quick-link-widget.component.html',
  styleUrls: ['./multi-icon-quick-link-widget.component.scss'],
})
export class MultiIconQuickLinkWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public multiIconQuickLinksData: {
    title: string;
    url: string | undefined;
    pageId: string | undefined;
    icon: string | undefined;
    role: string | undefined;
    zone: string | undefined;
    externalLink: string | undefined;
    browserBehaviour: string | undefined;
  } = {
    title: '',
    icon: undefined,
    url: undefined,
    pageId: undefined,
    role: '',
    zone: '',
    externalLink: undefined,
    browserBehaviour: undefined,
  };
  textContentStyle: baseCssConfigStyle;
  private quickLinkZones = [
    APP_ZONES.ONBOARDING,
    APP_ZONES.VERIFICATION,
    APP_ZONES.ORGANISATION_ONBOARDING,
    APP_ZONES.ORGANISATION_VERIFICATION,
  ];
  public headerSubheaderTemplate: string;
  public rightIcon: string;
  public leftIcon: string;
  @Select(RoleState.getRole)
  private roles!: Observable<RoleStateModel>;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public iconStyle: baseCssConfigStyle;

  constructor(
    private zonePageUrlProvider: ZonePageUrlProvider,
    private configService: ConfigService,
    public dataIn: ProviderClass<QuickLinksWidgetConfig>,
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private navigationService: NavigationService,
  ) {
    super();
    if (this.dataIn != undefined && this.dataIn != null) {
      this.attri = this.dataIn.attri ?? null;
      if (this.attri != null) {
        this.setPageUrl(this.dataIn);
        this.multiIconQuickLinksData.externalLink = this.dataIn.attri?.['externalUrl'] ?? null;
        this.multiIconQuickLinksData.browserBehaviour = this.dataIn.attri?.['browserBehaviour'] ?? null;
      }
    }
  }
  ngOnInit() {
    this.onLoadWidgetConfiguration();
  }

  private onLoadWidgetConfiguration(): void {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
    }
    this.leftIcon = this.attri?.['iconConfig']?.leftIcon;
    this.rightIcon = this.attri?.['iconConfig']?.rightIcon;

    this.headerSubheaderTemplate = this.attri?.['itemConfig']?.itemTemplate ?? '';
    const template = Handlebars.compile(this.headerSubheaderTemplate);
    buildHandleBarModel(
      this.store,
      this.headerSubheaderTemplate,
      this.countriesCurrenciesService,
      this.iconCacheService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.headerSubheaderTemplate = filterIfHandlebarNotRendered(template(model));
      });

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.V_ALIGN,
      BaseCssProperties.BOX_SHADOW,
    ]);

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);

    this.iconStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.ICON_COLOR,
      BaseCssProperties.ICON_SIZE,
    ]);
    this.getGlobalStyling();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }
  }

  public setPageUrl(dataIn: ProviderClass<QuickLinksWidgetConfig>) {
    let pageUrl: string | undefined;
    const pageId: string | undefined = dataIn.attri['pageId'] ?? undefined;

    if (pageId) {
      const { pageUrl: url, pageNavigationMetadata } = this.navigationService.buildPageNavigationUrl({
        pageId: pageId || '', // using this it will generate url based on pageId and ignore pageUrl, if pageId is present in widget config
        zone: dataIn.attri?.zone || '',
        pageUrl: pageUrl || '',
        role: dataIn.attri?.role || '',
        navigationOption: dataIn.attri['navigationOption'],
        tabRef: dataIn.attri?.['tabRef'] ?? '',
        pageTransition: dataIn.attri?.['pageTransition'] ?? 'forward',
        browserBehaviour: dataIn.attri?.['browserBehaviour'],
        externalUrl: dataIn.attri?.['externalUrl'],
      });

      pageUrl = this.checkRouteForParamsAndUpdatePageUrl(url) || '';

      this.multiIconQuickLinksData.url = pageUrl;

      this.multiIconQuickLinksData.role = pageNavigationMetadata?.persona || '';
      this.multiIconQuickLinksData.zone = pageNavigationMetadata?.zone || '';
      this.multiIconQuickLinksData.pageId = pageId;
      return; // Exit after handling pageId-based navigation
    }

    // ---------- Fallback to old URL structure if pageId-based navigation metadata is not found ----------

    this.navigateToOldUrlStructure(pageUrl, dataIn);
  }

  private navigateToOldUrlStructure(
    pageUrl: string | undefined,

    dataIn: ProviderClass<QuickLinksWidgetConfig>,
  ) {
    this.checkForDummyRole();

    if (dataIn.attri['pageUrl']) {
      pageUrl = dataIn.attri['pageUrl'];
      pageUrl = this.checkRouteForParamsAndUpdatePageUrl(pageUrl);
    }

    if (this.quickLinkZones.includes(dataIn.attri?.zone as APP_ZONES)) {
      this.multiIconQuickLinksData.url = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}/${pageUrl}/${
        dataIn.attri?.role
      }/default`;

      if (dataIn.attri['navigationOption'] === NavigationOptionEnum.USE_AS_MODAL) {
        const zoneUrl = formatNameAsUrl(dataIn.attri?.zone);
        this.multiIconQuickLinksData.url = createLoggedOutModalPageUrl({
          zoneUrl,
          pageUrl: pageUrl ?? '',
          role: dataIn.attri?.role,
          url: this.multiIconQuickLinksData.url,
        });
      }
    } else if (pageUrl) {
      const navigationOptions: NavigationOptionsType = {
        navigationOption: dataIn.attri['navigationOption'],
        zone: dataIn.attri?.zone || '',
        pageUrl: pageUrl,
        pageId: dataIn.attri?.pageId || '',
        role: dataIn.attri?.role || '',
        tabRef: dataIn.attri?.['tabRef'] || '',
        pageTransition: dataIn.attri?.['pageTransition'] || 'forward',
      };
      this.multiIconQuickLinksData.url = createRedirectionUrlFromNavigationOptions(navigationOptions);
    } else {
      this.multiIconQuickLinksData.url = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}`;
    }
    this.multiIconQuickLinksData.role = dataIn.attri?.role;
    this.multiIconQuickLinksData.zone = dataIn.attri?.zone;
  }

  /**
   * Checks if current url has id in route params and return updated page url
   * @param pageUrl - page url from widget config
   */
  checkRouteForParamsAndUpdatePageUrl(pageUrl: string): string | undefined {
    let url = pageUrl;
    if (this.zonePageUrlProvider?.['id']) {
      url = `${pageUrl}/${this.zonePageUrlProvider['id']}`;
    }
    return url;
  }

  checkForDummyRole() {
    if (this.dataIn?.attri?.role === DummyRole.EMPTY_ROLE) {
      this.roles
        .subscribe((roleInfo) => {
          this.dataIn.attri.role = roleInfo.role;
        })
        .unsubscribe();
    }
  }
}
